import React, { useState } from 'react';
import { styled } from '@mui/system';
import { Check, RotateCcw } from 'lucide-react';
import {
  Card,
  Body,
  Muted,
  PrimaryButton,
  SecondaryButton,
  ExplainBox,
  FxLine
} from '../components/ui';
import { motion, fadeUp, popIn } from '../components/motion';
import { scoreOrder } from '../engine/scoring';
import { L, t } from '../strings';

const Item = styled('button')(({ theme, state, delay }) => ({
  width: '100%',
  textAlign: 'left',
  display: 'flex',
  gap: 12,
  alignItems: 'flex-start',
  padding: '12px 14px',
  marginBottom: 8,
  borderRadius: 12,
  border: `2px solid ${state === 'placed' ? theme.palette.primary.main : '#e0e0e0'}`,
  backgroundColor: state === 'placed' ? 'rgba(121, 201, 18, 0.06)' : '#fff',
  fontFamily: 'Rubik-Regular',
  cursor: state === 'done' ? 'default' : 'pointer',
  transition:
    'border-color 160ms ease, background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
  ...motion(`${fadeUp} 360ms ease-out ${delay}ms both`),
  '&:hover':
    state === 'done'
      ? {}
      : {
          borderColor: theme.palette.primary.main,
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 16px rgba(34, 34, 34, 0.06)'
        },
  '&:focus-visible': { outline: `3px solid ${theme.palette.primary.light}`, outlineOffset: 1 }
}));

const Num = styled('span')(({ theme, active }) => ({
  width: 28,
  height: 28,
  minWidth: 28,
  borderRadius: 8,
  backgroundColor: active ? theme.palette.primary.salad : theme.palette.grey[100],
  color: active ? theme.palette.textPrimary.main : theme.palette.textDisabled.main,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  fontWeight: 500,
  ...(active ? motion(`${popIn} 280ms ease-out both`) : {})
}));

const Title = styled('div')(() => ({ fontSize: 16, fontWeight: 500, lineHeight: '22px' }));
const Detail = styled('div')(({ theme }) => ({
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.textSecondary.main,
  marginTop: 2
}));

const Compare = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
  marginTop: 12,
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' }
}));

const Col = styled('div')(({ theme, delay }) => ({
  padding: '12px 14px',
  borderRadius: 12,
  backgroundColor: theme.palette.grey[50],
  ...motion(`${fadeUp} 360ms ease-out ${delay}ms both`)
}));

const Line = styled('div')(({ theme, delay }) => ({
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  marginTop: 8,
  fontFamily: 'Rubik-Regular',
  fontSize: 15,
  lineHeight: '20px',
  color: theme.palette.textPrimary.main,
  ...motion(`${fadeUp} 300ms ease-out ${delay}ms both`)
}));

const MARK_BG = { ok: '#79c912', near: '#f2c94c', far: '#e5646f' };

// Exact position, off by one, or further.
const Mark = styled('span')(({ tone }) => ({
  minWidth: 22,
  height: 22,
  padding: '0 4px',
  boxSizing: 'border-box',
  borderRadius: 100,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 500,
  color: tone === 'near' ? '#222' : tone ? '#fff' : '#222',
  backgroundColor: MARK_BG[tone] || '#ebedee'
}));

export default function TriageStep({ step, answered, onSubmit, kpiLabels }) {
  const [order, setOrder] = useState([]);
  const byId = Object.fromEntries(step.items.map((i) => [i.id, i]));
  const done = !!answered;

  const toggle = (id) => {
    if (done) return;
    setOrder((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));
  };

  const submit = () => {
    const score = scoreOrder(order, step.correctOrder);
    onSubmit({ score, fx: score >= 0.7 ? step.fx?.ok : step.fx?.fail, order });
  };

  return (
    <Card>
      <Body style={{ marginBottom: 6 }}>{L(step.text)}</Body>
      <Muted style={{ marginBottom: 14 }}>{t('orderHint')}</Muted>
      {step.items.map((it, i) => {
        const pos = (done ? answered.order : order).indexOf(it.id);
        return (
          <Item
            key={it.id}
            type="button"
            delay={i * 60}
            state={done ? 'done' : pos >= 0 ? 'placed' : 'idle'}
            onClick={() => toggle(it.id)}>
            <Num key={pos} active={pos >= 0}>
              {pos >= 0 ? pos + 1 : '·'}
            </Num>
            <div>
              <Title>{L(it.title)}</Title>
              <Detail>{L(it.detail)}</Detail>
            </div>
          </Item>
        );
      })}
      {!done ? (
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <PrimaryButton disabled={order.length !== step.items.length} onClick={submit}>
            {t('submit')}
          </PrimaryButton>
          {order.length > 0 && (
            <SecondaryButton onClick={() => setOrder([])} startIcon={<RotateCcw size={15} />}>
              {t('reset')}
            </SecondaryButton>
          )}
        </div>
      ) : (
        <>
          <Compare>
            <Col delay={0}>
              <Muted>{t('yourOrder')}</Muted>
              {answered.order.map((id, i) => {
                const off = step.correctOrder.indexOf(id) - i;
                const tone = off === 0 ? 'ok' : Math.abs(off) === 1 ? 'near' : 'far';
                return (
                  <Line key={id} delay={100 + i * 70}>
                    <Mark tone={tone}>
                      {off === 0 ? <Check size={13} /> : `${off < 0 ? '↑' : '↓'}${Math.abs(off)}`}
                    </Mark>
                    {L(byId[id].title)}
                  </Line>
                );
              })}
            </Col>
            <Col delay={120}>
              <Muted>{t('correctOrder')}</Muted>
              {step.correctOrder.map((id, i) => (
                <Line key={id} delay={220 + i * 70}>
                  <Mark>{i + 1}</Mark>
                  {L(byId[id].title)}
                </Line>
              ))}
            </Col>
          </Compare>
          <ExplainBox
            ok={answered.score >= 0.7}
            label={t('pairsRight', { p: Math.round(answered.score * 100) })}>
            {L(step.explain)}
            <FxLine fx={answered.fx} kpiLabels={kpiLabels} />
          </ExplainBox>
        </>
      )}
    </Card>
  );
}
